import { Tabs as AntTabs } from 'antd';
import { isEmpty } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { IntegrationProvider, JiraApi, JiraConfig } from 'api/integrations';
import { UserType } from 'api/models';
import { TemplateId, TemplateState } from 'data/templates/api';
import { ProjectTemplateActiveRecord } from 'data/templates/projects';
import { saveProjectTemplate } from 'data/templates/projects/actions';
import { useUserType } from 'hooks/use-user-type';
import { unreachable } from 'utils/unreachable';
import { useActiveRecord } from '../commons';
import { KeyEventsTab } from '../key-events/key-events-tab';
import { TemplateEmptyState } from './empty-state';
import { ProjectTemplatePhaseDetailView } from './project-phase';
import { ProjectTemplateTaskDetailView } from './task';
import { JiraPane } from './task/integrations';

const { TabPane } = AntTabs;

const Tabs = styled(AntTabs)`
  > .ant-tabs-nav {
    background: var(--white-100);
  }
`;

const TabWithMargin = styled.div`
  height: 100%;
  width: 100%;
  margin-top: var(--spacing-05);
`;

const renderTabPane = (item: ProjectTemplateActiveRecord) => {
  switch (item.type) {
    case 'PROJECT_PHASE':
      return <ProjectTemplatePhaseDetailView projectPhaseId={item.id} />;
    case 'TASK':
      return <ProjectTemplateTaskDetailView taskId={item.id} />;
    default:
      return unreachable(item as never);
  }
};

const renderIntegrationsPane = (
  integrationConfig: JiraConfig,
  taskId: number,
) => {
  switch (integrationConfig.integrationType) {
    case IntegrationProvider.Jira:
      return <JiraPane jiraConfig={integrationConfig} taskId={taskId} />;

    default:
      return <></>;
  }
};

const renderTabs = (
  activeRecord: ProjectTemplateActiveRecord,
  templateId: TemplateId,
  jiraConfig?: JiraConfig,
) => {
  switch (activeRecord.type) {
    case 'PROJECT_PHASE':
      return (
        <>
          <TabPane
            tab={
              <FormattedMessage
                id={`templates.tab.${activeRecord.type.toLowerCase()}`}
              />
            }
            key="1"
          >
            <TabWithMargin>{renderTabPane(activeRecord)}</TabWithMargin>
          </TabPane>

          <TabPane
            tab={<FormattedMessage id="templates.tab.key_events" />}
            key="2"
          >
            <KeyEventsTab
              refType="phase"
              refId={activeRecord.id}
              templateId={templateId as number}
            />
          </TabPane>
        </>
      );
    case 'TASK':
      return (
        <>
          <TabPane
            tab={
              <FormattedMessage
                id={`templates.tab.${activeRecord.type.toLowerCase()}`}
              />
            }
            key="1"
          >
            <TabWithMargin>{renderTabPane(activeRecord)}</TabWithMargin>
          </TabPane>
          <TabPane
            tab={<FormattedMessage id="templates.tab.key_events" />}
            key="2"
          >
            <KeyEventsTab
              refType="task"
              refId={activeRecord.id}
              templateId={templateId as number}
            />
          </TabPane>
          {jiraConfig && (
            <TabPane
              tab={<FormattedMessage id="templates.tab.jira" />}
              key={activeRecord.id}
            >
              {renderIntegrationsPane(jiraConfig, activeRecord.id)}
            </TabPane>
          )}
        </>
      );
    default:
      return <></>;
  }
};

export const ProjectTemplateDetailView: FC<{ templateId: TemplateId }> = ({
  templateId,
}) => {
  const userType = useUserType();
  const dispatch = useDispatch();

  const activeRecord = useActiveRecord();
  const [activeTab, setActiveTab] = useState('1');
  const [jiraConfig, setJiraConfig] = useState<JiraConfig>();

  useEffect(() => {
    if (userType === UserType.Native) {
      JiraApi.getJiraIntegration().then((res) => {
        if (!isEmpty(res)) {
          setJiraConfig(res[0]);
        }
      });
    }
  }, [userType]);

  return activeRecord ? (
    <Tabs
      defaultActiveKey={activeTab}
      onChange={(activeKey) => {
        if (activeKey === '2') {
          dispatch(
            saveProjectTemplate({
              type: TemplateState.Published,
            }),
          );
          setActiveTab(activeKey);
        }
      }}
    >
      {renderTabs(activeRecord, templateId, jiraConfig)}
    </Tabs>
  ) : (
    <TemplateEmptyState />
  );
};
